import API_CONFIG from '../config/api.config';

const AUTH_TOKEN_KEY = 'gl.auth.token';
const AUTH_USER_KEY = 'gl.auth.user';

/**
 * Login with username and password
 * @param {string} username 
 * @param {string} password 
 * @returns {Promise<{success: boolean, data?: object, message?: string}>}
 */
export async function login(username, password) {
  try {
    const response = await fetch(`${API_CONFIG.baseURL}/login`, {
      method: 'POST',
      headers: API_CONFIG.headers,
      body: JSON.stringify({
        username,
        password
      })
    });

    const data = await response.json();

    if (data.success && data.data) {
      // Store auth token and user data
      localStorage.setItem(AUTH_TOKEN_KEY, data.data.auth_token);
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(data.data));
      
      return {
        success: true,
        data: data.data,
        message: data.response
      };
    } else {
      return {
        success: false,
        message: data.response || 'Login failed'
      };
    }
  } catch (error) {
    console.error('Login error:', error);
    return {
      success: false,
      message: 'Network error. Please try again.'
    };
  }
}

/**
 * Logout current user and delete token from database
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export async function logout() {
  const token = getAuthToken();
  
  if (token) {
    try {
      // Call API to delete token from database
      const response = await apiRequest('logout', {
        method: 'POST'
      });
      
      // Clear local storage regardless of API response
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(AUTH_USER_KEY);
      
      return {
        success: response.success || true,
        message: response.response || 'Logged out successfully'
      };
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local storage even if API fails
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(AUTH_USER_KEY);
      
      return {
        success: true,
        message: 'Logged out locally'
      };
    }
  } else {
    // No token, just clear storage
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    
    return {
      success: true,
      message: 'Already logged out'
    };
  }
}

/**
 * Get current auth token
 * @returns {string|null}
 */
export function getAuthToken() {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

/**
 * Get current user data
 * @returns {object|null}
 */
export function getCurrentUser() {
  try {
    const userData = localStorage.getItem(AUTH_USER_KEY);
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
}

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export function isAuthenticated() {
  return !!getAuthToken();
}

/**
 * Make authenticated API request
 * @param {string} endpoint 
 * @param {object} options 
 * @returns {Promise<any>}
 */
export async function apiRequest(endpoint, options = {}) {
  const token = getAuthToken();
  
  const headers = {
    ...API_CONFIG.headers,
    ...(options.headers || {})
  };

  // Add auth token if available
  if (token) {
    headers['auth'] = token;
  }

  try {
    const response = await fetch(`${API_CONFIG.baseURL}/${endpoint}`, {
      ...options,
      headers
    });

    return await response.json();
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
}
